/**
 * Revision R3 — „Freigaben": die zentrale Verwaltung der SELBST vergebenen
 * Zugriffe (Gegenstück zu „Für mich freigegeben"). Der Server liefert alle
 * ACL-Einträge des Workspace, GEFILTERT durch den zentralen Guard (sichtbar
 * ist nur, was der Aufrufer am Ziel selbst verwalten dürfte — manage_acl,
 * fail-closed pro Zeile). Rolle ändern / entziehen läuft über dieselben
 * ACL-Endpoints wie der Freigabe-Dialog — keine Zweitimplementierung.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { navigate } from "../router";
import { useRyfe } from "../RyfeContext";
import { routeForKind } from "../open-file";

import "./ryfe.scss";

import type { PendingInvite, ShareOverviewEntry } from "../api";

// R4 (Punkt 8.2) — eine ausstehende E-Mail-Einladung in der Übersicht.
// v1.6 — kann jetzt auch einen Ordner betreffen (Ordner-Einladung).
type PendingRow = PendingInvite & {
  target: {
    type: "file" | "folder";
    id: string;
    name: string;
    kind?: string;
    isRoot?: boolean;
  };
};

const ROLE_LABELS: Record<string, string> = {
  owner: "Mitbesitzer",
  editor: "Bearbeiten",
  viewer: "Lesen",
};

const targetIcon = (t: ShareOverviewEntry["target"]) =>
  t.type === "folder" ? "📁" : t.kind === "board" ? "🗒" : "📄";

export const RyfeShares = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [entries, setEntries] = useState<ShareOverviewEntry[] | null>(null);
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    try {
      const r = await ryfeApi.listShares(wsId);
      setEntries(r.entries);
      setPending(r.pending || []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  }, [wsId]);

  useEffect(() => {
    void load();
  }, [load]);

  const changeRole = async (entry: ShareOverviewEntry, role: string) => {
    if (!wsId) {
      return;
    }
    setError(null);
    try {
      const body = {
        principalType: entry.principalType,
        principalId: entry.principalId,
        role,
      };
      if (entry.target.type === "file") {
        await ryfeApi.setFileAcl(wsId, entry.target.id, body);
      } else {
        await ryfeApi.setFolderAcl(wsId, entry.target.id, body);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Ändern fehlgeschlagen");
    }
  };

  const revoke = async (entry: ShareOverviewEntry) => {
    if (!wsId) {
      return;
    }
    if (
      !window.confirm(
        `Freigabe von „${entry.target.name}“ für ${entry.label} entziehen?`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      const body = {
        principalType: entry.principalType,
        principalId: entry.principalId,
      };
      if (entry.target.type === "file") {
        await ryfeApi.removeFileAcl(wsId, entry.target.id, body);
      } else {
        await ryfeApi.removeFolderAcl(wsId, entry.target.id, body);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Entzug fehlgeschlagen");
    }
  };

  // R4 (Punkt 8.2) — Einladung widerrufen (ausstehend, noch ohne Konto).
  const revokePending = async (p: PendingRow) => {
    if (!wsId) {
      return;
    }
    if (
      !window.confirm(
        `Einladung von „${p.target.name}“ für ${p.email} widerrufen?`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      if (p.target.type === "folder") {
        await ryfeApi.revokeFolderInvite(wsId, p.target.id, p.email);
      } else {
        await ryfeApi.revokeInvite(wsId, p.target.id, p.email);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Widerruf fehlgeschlagen");
    }
  };

  const openTarget = (t: ShareOverviewEntry["target"]) => {
    if (t.type === "folder") {
      navigate(t.isRoot ? "/files" : `/files/${t.id}`);
      return;
    }
    const route = routeForKind(t.kind, t.id);
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Freigaben</h1>
      <p className="ryfe-muted" style={{ paddingTop: 0 }}>
        Alle Zugriffe, die in diesem Workspace auf Ordner und Dateien vergeben
        sind — sichtbar ist, was du selbst verwalten darfst. Snapshot-Links
        (eingefroren, verschlüsselt) sind davon unabhängig.
      </p>

      {error && <div className="ryfe-auth__error">{error}</div>}

      {!entries ? (
        <div className="ryfe-muted">Laden…</div>
      ) : entries.length === 0 && pending.length === 0 ? (
        <div className="ryfe-muted">
          Noch keine Freigaben vergeben — teile einen Ordner oder eine Datei
          über „Teilen…" bzw. die Freigabeeinstellung.
        </div>
      ) : (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Objekt</th>
                <th>Typ</th>
                <th>Freigegeben für</th>
                <th>Rolle</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr className="ryfe-row" key={e.id}>
                  <td>
                    <button
                      className="ryfe-linkish"
                      onClick={() => openTarget(e.target)}
                    >
                      {targetIcon(e.target)} {e.target.name}
                    </button>
                  </td>
                  <td>
                    {e.target.type === "folder"
                      ? "Ordner"
                      : e.target.kind || "Datei"}
                  </td>
                  <td>
                    {e.principalType === "mcp" ? "🤖 " : ""}
                    {e.label}
                  </td>
                  <td>
                    {e.principalType === "user" ? (
                      <select
                        className="ryfe-select"
                        value={e.role}
                        onChange={(ev) => void changeRole(e, ev.target.value)}
                        aria-label={`Rolle für ${e.label} an ${e.target.name}`}
                      >
                        <option value="owner">{ROLE_LABELS.owner}</option>
                        <option value="editor">{ROLE_LABELS.editor}</option>
                        <option value="viewer">{ROLE_LABELS.viewer}</option>
                      </select>
                    ) : (
                      <span>
                        {ROLE_LABELS[e.role] || e.role}
                        {e.principalType === "mcp" && " · via MCP-Token-Seite"}
                      </span>
                    )}
                  </td>
                  <td>
                    {e.principalType === "user" && (
                      <button
                        className="ryfe-chip"
                        onClick={() => void revoke(e)}
                        title="Freigabe entziehen"
                      >
                        Entziehen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {/* R4 (Punkt 8.2) — ausstehende Einladungen: sichtbar & widerrufbar. */}
              {pending.map((p) => (
                <tr className="ryfe-row" key={`pending-${p.id}`}>
                  <td>
                    <button
                      className="ryfe-linkish"
                      onClick={() => openTarget(p.target)}
                    >
                      {targetIcon(p.target)} {p.target.name}
                    </button>
                  </td>
                  <td>{p.target.kind || "Datei"}</td>
                  <td>
                    ✉️ {p.email}{" "}
                    <span
                      className="ryfe-badge ryfe-badge--warn"
                      title="Eingeladen — noch kein Konto. Wird beim Registrieren automatisch aktiv."
                    >
                      eingeladen
                    </span>
                  </td>
                  <td>{ROLE_LABELS[p.role] || p.role}</td>
                  <td>
                    <button
                      className="ryfe-chip"
                      onClick={() => void revokePending(p)}
                      title="Einladung widerrufen"
                    >
                      Widerrufen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
